// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, subDays, startOfDay } from 'date-fns';

interface AdminAnalyticsProps {
  session: any;
  profile: any;
}

export const AdminAnalytics: React.FC<AdminAnalyticsProps> = ({ session, profile }) => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [userGrowth, setUserGrowth] = useState<Array<{ date: string; users: number }>>([]);
  const [subscriptionTrends, setSubscriptionTrends] = useState<
    Array<{ date: string; subscriptions: number }>
  >([]);
  const [notificationStats, setNotificationStats] = useState<
    Array<{ date: string; sent: number; failed: number }>
  >([]);
  const [topicPopularity, setTopicPopularity] = useState<
    Array<{ name: string; subscriptions: number }>
  >([]);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const getDaysBack = () => {
    switch (dateRange) {
      case '7d':
        return 7;
      case '30d':
        return 30;
      case '90d':
        return 90;
      case '1y':
        return 365;
      default:
        return 30;
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const daysBack = getDaysBack();
      const startDate = startOfDay(subDays(new Date(), daysBack));

      // User growth
      // Note: profiles table doesn't have created_at, using updated_at instead
      // For accurate creation dates, we'd need to join with auth.users
      const { data: users } = await supabase
        .from('profiles')
        .select('updated_at')
        .gte('updated_at', startDate.toISOString())
        .order('updated_at', { ascending: true });

      if (users) {
        const dateFormat = daysBack <= 7 ? 'MMM dd' : daysBack <= 30 ? 'MMM dd' : 'MMM yyyy';
        const interval = daysBack <= 7 ? 1 : daysBack <= 30 ? 1 : daysBack <= 90 ? 7 : 30;

        const userGrowthData: Array<{ date: string; users: number }> = [];
        for (let i = 0; i < daysBack; i += interval) {
          const date = subDays(new Date(), daysBack - i - 1);
          const count = users.filter((u) => {
            const userDate = new Date(u.updated_at);
            return userDate <= date;
          }).length;
          userGrowthData.push({
            date: format(date, dateFormat),
            users: count,
          });
        }
        setUserGrowth(userGrowthData);
      }

      // Subscription trends
      const { data: subscriptions } = await supabase
        .from('user_topic_subscriptions')
        .select('created_at')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (subscriptions) {
        const dateFormat = daysBack <= 7 ? 'MMM dd' : daysBack <= 30 ? 'MMM dd' : 'MMM yyyy';
        const interval = daysBack <= 7 ? 1 : daysBack <= 30 ? 1 : daysBack <= 90 ? 7 : 30;

        const subscriptionData: Array<{ date: string; subscriptions: number }> = [];
        for (let i = 0; i < daysBack; i += interval) {
          const date = subDays(new Date(), daysBack - i - 1);
          const count = subscriptions.filter((s) => {
            if (!s.created_at) return false;
            const subDate = new Date(s.created_at);
            return !isNaN(subDate.getTime()) && subDate <= date;
          }).length;
          subscriptionData.push({
            date: format(date, dateFormat),
            subscriptions: count,
          });
        }
        setSubscriptionTrends(subscriptionData);
      }

      // Notification stats
      const { data: notifications } = await supabase
        .from('notifications')
        .select('created_at, email_sent')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (notifications) {
        const dateFormat = daysBack <= 7 ? 'MMM dd' : daysBack <= 30 ? 'MMM dd' : 'MMM yyyy';
        const interval = daysBack <= 7 ? 1 : daysBack <= 30 ? 1 : daysBack <= 90 ? 7 : 30;

        const notificationData: Array<{ date: string; sent: number; failed: number }> = [];
        for (let i = 0; i < daysBack; i += interval) {
          const date = subDays(new Date(), daysBack - i - 1);
          const dateStr = format(date, dateFormat);
          const dayNotifications = notifications.filter((n) => {
            if (!n.created_at) return false;
            try {
              const notifDate = new Date(n.created_at);
              if (isNaN(notifDate.getTime())) return false;
              return format(notifDate, dateFormat) === dateStr;
            } catch (e) {
              return false;
            }
          });

          notificationData.push({
            date: dateStr,
            sent: dayNotifications.filter((n) => n.email_sent).length,
            failed: dayNotifications.filter((n) => !n.email_sent).length,
          });
        }
        setNotificationStats(notificationData);
      }

      // Topic popularity
      const { data: topicSubs } = await supabase
        .from('user_topic_subscriptions')
        .select('law_topics:topic_id(name)');

      if (topicSubs) {
        const topicCounts: Record<string, number> = {};
        topicSubs.forEach((sub: any) => {
          const topicName = sub.law_topics?.name || 'Unknown';
          topicCounts[topicName] = (topicCounts[topicName] || 0) + 1;
        });

        setTopicPopularity(
          Object.entries(topicCounts)
            .map(([name, subscriptions]) => ({ name, subscriptions }))
            .sort((a, b) => b.subscriptions - a.subscriptions)
            .slice(0, 10)
        );
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luna-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
            Advanced Analytics
          </h1>
          <p className="text-sm sm:text-base text-luna-text-light/70 dark:text-luna-text-dark/70">
            Detailed insights and metrics
          </p>
        </div>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value as any)}
          className="w-full sm:w-auto px-4 py-3 sm:py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark text-base sm:text-sm min-h-[44px] sm:min-h-[40px] touch-manipulation"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="1y">Last year</option>
        </select>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* User Growth */}
        <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-luna-text-light dark:text-luna-text-dark">
            User Growth
          </h3>
          <div className="h-[250px] sm:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={userGrowth} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={60} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} width={40} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="users"
                  stroke="#8884d8"
                  fill="#8884d8"
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Subscription Trends */}
        <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-luna-text-light dark:text-luna-text-dark">
            Subscription Trends
          </h3>
          <div className="h-[250px] sm:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={subscriptionTrends} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={60} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} width={40} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="subscriptions"
                  stroke="#82ca9d"
                  strokeWidth={2}
                  dot={{ fill: '#82ca9d', r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Notification Stats */}
        <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-luna-text-light dark:text-luna-text-dark">
            Notification Performance
          </h3>
          <div className="h-[250px] sm:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={notificationStats} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={60} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} width={40} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="sent" fill="#82ca9d" name="Sent" />
                <Bar dataKey="failed" fill="#ff7300" name="Failed" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Topic Popularity */}
        <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-luna-text-light dark:text-luna-text-dark">
            Most Popular Topics
          </h3>
          <div className="h-[250px] sm:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topicPopularity} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" stroke="#6b7280" tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" stroke="#6b7280" width={80} tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Bar dataKey="subscriptions" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

